import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Clock, LogIn, LogOut, Coffee, CheckCircle, User } from 'lucide-react';
import { supabaseClient } from './lib/supabaseClient';
import {
  persistPunchIn,
  persistPunchOut,
  type AttendancePunchInput
} from './lib/attendancePunchPersistence';
import { EmployeeSummary, AttendanceRecord, AttendanceStatus } from './types/models';
import EmployeeOvertimePanel from './overtime/EmployeeOvertimePanel';
import EmployeeOvertimeHistory from './overtime/EmployeeOvertimeHistory';
import useEmployeeAttendanceCalculation from './overtime/useEmployeeAttendanceCalculation';
import { calculateAttendanceMinutes, calculateEffectiveBreakMinutes, resolveMinuteMode } from './lib/attendanceCalculation';
import {
  buildRecentDateStrings,
  buildRecordKeyCandidates,
  findRecordByKeys,
  findOpenRecordByDates,
  getDateStringFromDate,
  getLocalDateString,
  resolveShiftTypeForPunchOut
} from './lib/attendanceUtils';
import {
  ATTENDANCE_EVENT_NAME,
  ATTENDANCE_STORAGE_KEY,
  type AttendanceBroadcastDetail,
  type AttendanceBroadcastContext
} from './constants/attendanceSync';

const SESSION_STORAGE_KEY = 'satsuki-employee-session';
const RESUME_LOOKBACK_DAYS = 7;

/**
 * Check if an open attendance record is "stale" (should not block new clock-in).
 * Stale = from a previous day and not a valid ongoing night shift.
 * - Today's record: never stale
 * - Yesterday's record with clock-in >= 16:00 and current time < 12:00: valid night shift
 * - Everything else: stale
 */
const isStaleOpenRecord = (
  recordDate: string | null,
  clockIn: string | null,
  now: Date
): boolean => {
  if (!recordDate || !clockIn) return false;
  const todayStr = getDateStringFromDate(now);
  if (recordDate === todayStr) return false;
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayStr = getDateStringFromDate(yesterday);
  if (recordDate === yesterdayStr) {
    const [hour, min] = clockIn.split(':').map(Number);
    if ((hour > 15 || (hour === 15 && min >= 30)) && now.getHours() < 12) return false;
  }
  return true;
};
type StepState = 'select' | 'punched' | 'break' | 'complete';

type PendingPunchOut = Omit<AttendancePunchInput, 'clockOutAt' | 'dutyType' | 'status'> & {
  clockOutAt: string;
  dutyType: 'day' | 'night';
  status: '確定';
};
type PendingPunchIn = Omit<AttendancePunchInput, 'clockOutAt' | 'dutyType' | 'status'> & {
  clockOutAt: null;
  dutyType: 'day' | 'night';
  status: 'working';
};

const generateEmployeeIdentifier = (): string => {
  const now = new Date();
  const timestamp = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1)
    .toString()
    .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now
      .getHours()
      .toString()
      .padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
  const random = Math.random().toString(36).toUpperCase().slice(2, 6);
  return `EMP-${timestamp}-${random}`;
};

const isShiftState = (value: unknown): value is 'day' | 'night' =>
  value === 'day' || value === 'night';

type RemoteAttendanceRow = {
  id: string | null;
  employee_id?: string | null;
  employeeId?: string | null;
  employee_code?: string | null;
  employeeCode?: string | null;
  employee_name?: string | null;
  employeeName?: string | null;
  work_date?: string | null;
  date?: string | null;
  attendance_date?: string | null;
  workDate?: string | null;
  clock_in_at?: string | null;
  clock_in?: string | null;
  clockIn?: string | null;
  clock_in_time?: string | null;
  clockInTime?: string | null;
  clock_out_at?: string | null;
  clock_out?: string | null;
  clockOut?: string | null;
  clock_out_time?: string | null;
  clockOutTime?: string | null;
  break_minutes?: number | null;
  breakMinutes?: number | null;
  duty_type?: string | null;
  shift_type?: string | null;
  shiftType?: string | null;
  dutyType?: string | null;
  status?: string | null;
  notes?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
  updated_at?: string | null;
  updatedAt?: string | null;
  employee?: {
    employeeId?: string | null;
    name?: string | null;
  } | null;
};

const toHHMMorNull = (value: unknown): string | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return value.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return null;
  }
  if (/^\d{1,2}:\d{2}/.test(text)) {
    return text.slice(0, 5);
  }
  const isoCandidate = text.includes('T') ? text : text.replace(' ', 'T');
  const parsed = new Date(isoCandidate);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  if (text.includes('T')) {
    const timePart = text.split('T')[1];
    if (timePart) {
      const [hh, mm] = timePart.split(':');
      if (hh && mm) {
        return `${hh}:${mm}`;
      }
    }
  }
  if (text.includes(':')) {
    return text.slice(0, 5);
  }
  return null;
};

const computeTotalMinutesFromHHMM = (
  clockIn: string | null,
  clockOut: string | null,
  breakMinutes: number,
  shiftType: AttendanceRecord['shiftType']
): number => {
  if (!clockIn || !clockOut) {
    return 0;
  }

  const [inHour, inMin] = clockIn.split(':').map(Number);
  const [outHour, outMin] = clockOut.split(':').map(Number);

  let total = (outHour * 60 + outMin) - (inHour * 60 + inMin);

  if (total < 0 || shiftType === 'night') {
    total += 24 * 60;
  }

  total -= breakMinutes;
  return Math.max(0, total);
};

const normalizeAttendanceRow = (
  row: RemoteAttendanceRow
): { key: string; record: AttendanceRecord } | null => {
  const rawEmployeeId = row.employee_id ?? row.employeeId ?? null;
  const rawEmployeeCode = row.employee_code ?? row.employeeCode ?? null;
  const dateValue = row.work_date ?? row.date ?? row.attendance_date ?? row.workDate;

  if (!dateValue) {
    return null;
  }

  const employeeCode = String(rawEmployeeCode ?? rawEmployeeId ?? '');
  if (!employeeCode) {
    return null;
  }

  const employeeInternalId = rawEmployeeId ? String(rawEmployeeId) : null;
  const recordDate = String(dateValue);
  const recordKey = `${employeeInternalId ?? employeeCode}-${recordDate}`;
  const shiftRaw = row.duty_type ?? row.shift_type ?? row.shiftType ?? row.dutyType ?? 'day';
  const normalizedShift = shiftRaw ? String(shiftRaw) : 'day';
  const statusRaw = row.status ?? 'working';
  const clockInValue = toHHMMorNull(
    row.clock_in_at ?? row.clock_in ?? row.clockIn ?? row.clock_in_time ?? row.clockInTime
  );
  const clockOutValue = toHHMMorNull(
    row.clock_out_at ?? row.clock_out ?? row.clockOut ?? row.clock_out_time ?? row.clockOutTime
  );
  const normalizedBreakMinutes = Number(row.break_minutes ?? row.breakMinutes ?? 0);

  return {
    key: recordKey,
    record: {
      id: row.id ? String(row.id) : null,
      employeeCode,
      employeeName: String(row.employee_name ?? row.employeeName ?? row.employee?.name ?? employeeCode),
      employeeInternalId,
      employee: row.employee
        ? {
          employeeId: String(row.employee.employeeId ?? employeeInternalId ?? employeeCode),
          name: String(row.employee.name ?? employeeCode)
        }
        : null,
      date: recordDate,
      clockIn: clockInValue,
      clockOut: clockOutValue,
      breakMinutes: normalizedBreakMinutes,
      totalMinutes: computeTotalMinutesFromHHMM(
        clockInValue,
        clockOutValue,
        normalizedBreakMinutes,
        normalizedShift as AttendanceRecord['shiftType']
      ),
      shiftType: normalizedShift as AttendanceRecord['shiftType'],
      status: statusRaw as AttendanceRecord['status'],
      notes: row.notes ?? null,
      createdAt: row.created_at ?? row.createdAt ?? null,
      updatedAt: row.updated_at ?? row.updatedAt ?? null
    }
  };
};

const replaceSelectedEmployeeAttendanceRecords = (
  records: Record<string, AttendanceRecord>,
  employee: EmployeeSummary,
  dateStrings: string[],
  replacements: Record<string, AttendanceRecord>
): Record<string, AttendanceRecord> => {
  const next = { ...records };

  dateStrings.forEach((date) => {
    buildRecordKeyCandidates(employee, date).forEach((key) => {
      delete next[key];
    });
  });

  return {
    ...next,
    ...replacements
  };
};

const EmployeeAttendanceTool = () => {
  const [currentDateTime, setCurrentDateTime] = useState<Date>(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [employeeCodeInput, setEmployeeCodeInput] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<StepState>('select');
  const [punchType, setPunchType] = useState<string>('');
  const [punchTime, setPunchTime] = useState<Date | null>(null);
  const [breakMinutes, setBreakMinutes] = useState<number>(0);
  const [workingMinutes, setWorkingMinutes] = useState<number>(0);
  const [clockInTime, setClockInTime] = useState<Date | null>(null);
  const [shiftType, setShiftType] = useState<'day' | 'night'>('day');

  // Supabaseから取得した従業員リスト
  const [activeEmployees, setActiveEmployees] = useState<EmployeeSummary[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState<boolean>(true);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  // 既存の出勤記録を管理（実際にはSupabaseから取得）
  const [attendanceRecords, setAttendanceRecords] = useState<Record<string, AttendanceRecord>>({});
  const [attendanceLoading, setAttendanceLoading] = useState<boolean>(true);
  const [attendanceError, setAttendanceError] = useState<string | null>(null);
  const [punchSaving, setPunchSaving] = useState<boolean>(false);
  const [pendingPunchIn, setPendingPunchIn] = useState<PendingPunchIn | null>(null);
  const [pendingPunchOut, setPendingPunchOut] = useState<PendingPunchOut | null>(null);
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState<boolean>(false);
  const [completedAttendanceId, setCompletedAttendanceId] = useState<string | null>(null);
  const [overtimeEligible, setOvertimeEligible] = useState<boolean | null>(null);
  const [overtimeSaving, setOvertimeSaving] = useState(false);
  const calculationEmployee = activeEmployees.find(emp => emp.id === selectedEmployee || emp.employeeCode === selectedEmployee);
  const attendanceCalculation = useEmployeeAttendanceCalculation(
    supabaseClient, calculationEmployee?.employeeCode ?? '', currentStep === 'complete' ? completedAttendanceId ?? '' : ''
  );
  const displayWork = useMemo(() => {
    const config = attendanceCalculation.config;
    if (!config || !clockInTime || !calculationEmployee) return null;
    const end = currentStep === 'complete' ? punchTime : currentDateTime;
    if (!end) return null;
    const options = {
      clockIn: clockInTime.toISOString(), clockOut: end.toISOString(),
      roundUpClockIn: config.roundUpClockIn,
      minuteMode: resolveMinuteMode(config.rules, calculationEmployee.id, getDateStringFromDate(clockInTime))
    };
    const elapsed = calculateAttendanceMinutes({ ...options, breakMinutes: 0 });
    const effectiveBreak = calculateEffectiveBreakMinutes(elapsed, breakMinutes, shiftType, config);
    return { minutes: calculateAttendanceMinutes({ ...options, breakMinutes: effectiveBreak }), breakMinutes: effectiveBreak };
  }, [attendanceCalculation.config, clockInTime, calculationEmployee, currentStep, punchTime, currentDateTime, breakMinutes, shiftType]);

  // 有給申請モーダル
  const [showLeaveRequestModal, setShowLeaveRequestModal] = useState<boolean>(false);
  const [leaveRequestEmployee, setLeaveRequestEmployee] = useState<string>('');
  const [leaveRequestEmployeeCode, setLeaveRequestEmployeeCode] = useState<string>('');
  const [leaveStartDate, setLeaveStartDate] = useState<string>('');
  const [leaveEndDate, setLeaveEndDate] = useState<string>('');
  const [leaveRequestLoading, setLeaveRequestLoading] = useState<boolean>(false);
  const [leaveRequestError, setLeaveRequestError] = useState<string | null>(null);
  const [leaveRequestSuccess, setLeaveRequestSuccess] = useState<boolean>(false);
  const employeeDropdownRef = useRef<HTMLDivElement | null>(null);
  const punchActionInFlightRef = useRef<boolean>(false);
  const punchOutResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filteredEmployees = useMemo(() => {
    if (!employeeCodeInput) {
      return activeEmployees;
    }
    const normalized = employeeCodeInput.toLowerCase();
    return activeEmployees.filter((emp) =>
      emp.employeeCode ? emp.employeeCode.toLowerCase().startsWith(normalized) : false
    );
  }, [activeEmployees, employeeCodeInput]);
  const resolveEmployee = (value: string): EmployeeSummary | undefined => {
    if (!value) {
      return undefined;
    }
    return activeEmployees.find(
      (emp) =>
        emp.id === value ||
        (emp.employeeCode && emp.employeeCode === value)
    );
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const storedRecords = window.localStorage.getItem(ATTENDANCE_STORAGE_KEY);
      if (!storedRecords) return;
      const parsed = JSON.parse(storedRecords) as Record<string, AttendanceRecord>;
      if (parsed && typeof parsed === 'object') {
        setAttendanceRecords(parsed);
      }
    } catch (err) {
      console.warn('Failed to restore attendance cache', err);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(SESSION_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as {
        selectedEmployee?: string;
        employeeCodeInput?: string;
        currentStep?: StepState;
        punchType?: string;
        clockInTime?: string | null;
        punchTime?: string | null;
        breakMinutes?: number;
        shiftType?: AttendanceRecord['shiftType'];
        pendingPunchIn?: PendingPunchIn | null;
        pendingPunchOut?: PendingPunchOut | null;
      };

      if (parsed.selectedEmployee) {
        setSelectedEmployee(parsed.selectedEmployee);
      }
      if (typeof parsed.employeeCodeInput === 'string') {
        setEmployeeCodeInput(parsed.employeeCodeInput);
      }
      if (parsed.currentStep && (['select', 'punched', 'break'] as StepState[]).includes(parsed.currentStep)) {
        setCurrentStep(parsed.currentStep);
      }
      if (parsed.punchType) {
        setPunchType(parsed.punchType);
      }
      if (parsed.breakMinutes !== undefined) {
        setBreakMinutes(parsed.breakMinutes);
      }
      if (isShiftState(parsed.shiftType)) {
        setShiftType(parsed.shiftType);
      }
      if (
        parsed.pendingPunchIn &&
        typeof parsed.pendingPunchIn.employeeCode === 'string' &&
        typeof parsed.pendingPunchIn.workDate === 'string' &&
        typeof parsed.pendingPunchIn.clockInAt === 'string' &&
        parsed.pendingPunchIn.clockOutAt === null &&
        isShiftState(parsed.pendingPunchIn.dutyType) &&
        parsed.pendingPunchIn.status === 'working'
      ) {
        setPendingPunchIn(parsed.pendingPunchIn);
      }
      if (
        parsed.pendingPunchOut &&
        typeof parsed.pendingPunchOut.employeeCode === 'string' &&
        typeof parsed.pendingPunchOut.workDate === 'string' &&
        typeof parsed.pendingPunchOut.clockInAt === 'string' &&
        typeof parsed.pendingPunchOut.clockOutAt === 'string' &&
        isShiftState(parsed.pendingPunchOut.dutyType) &&
        parsed.pendingPunchOut.status === '確定'
      ) {
        setPendingPunchOut(parsed.pendingPunchOut);
        setCurrentStep('break');
      }
      if (parsed.clockInTime) {
        const restoredClockIn = new Date(parsed.clockInTime);
        if (!Number.isNaN(restoredClockIn.getTime())) {
          setClockInTime(restoredClockIn);
          const diffMs = Date.now() - restoredClockIn.getTime();
          if (diffMs > 0) {
            setWorkingMinutes(Math.floor(diffMs / 1000 / 60));
          }
        }
      }
      if (parsed.punchTime) {
        const restoredPunchTime = new Date(parsed.punchTime);
        if (!Number.isNaN(restoredPunchTime.getTime())) {
          setPunchTime(restoredPunchTime);
        }
      }
    } catch (err) {
      console.warn('Failed to restore session state', err);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!selectedEmployee && currentStep === 'select') {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }
    const payload = {
      selectedEmployee,
      employeeCodeInput,
      currentStep,
      punchType,
      clockInTime: clockInTime ? clockInTime.toISOString() : null,
      punchTime: punchTime ? punchTime.toISOString() : null,
      breakMinutes,
      shiftType,
      pendingPunchIn,
      pendingPunchOut
    };
    window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
  }, [selectedEmployee, employeeCodeInput, currentStep, punchType, clockInTime, punchTime, breakMinutes, shiftType, pendingPunchIn, pendingPunchOut]);

  useEffect(() => {
    return () => {
      if (punchOutResetTimerRef.current) {
        clearTimeout(punchOutResetTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());

      if (clockInTime && currentStep === 'punched') {
        const now = new Date();
        const diffMs = now.getTime() - clockInTime.getTime();
        setWorkingMinutes(Math.floor(diffMs / 1000 / 60));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [clockInTime, currentStep]);

  useEffect(() => {
    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      setEmployeesError(null);

      try {
        if (!supabaseClient) {
          setEmployeesError('SupabaseのURLまたは匿名キーが設定されていません。`.env.local` を確認してください。');
          setActiveEmployees([]);
          return;
        }

        const MAX_RETRIES = 5;
        let lastError: any = null;
        let data: any[] | null = null;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          const result = await supabaseClient
            .from('employees')
            .select('*')
            .order('name', { ascending: true });

          if (!result.error) {
            data = result.data;
            lastError = null;
            break;
          }

          lastError = result.error;
          console.warn(`[Retry ${attempt + 1}/${MAX_RETRIES}] employees fetch failed:`, result.error.message);

          if (attempt < MAX_RETRIES - 1) {
            const delay = Math.min(2000 * Math.pow(2, attempt), 30000);
            await new Promise(r => setTimeout(r, delay));
          }
        }

        if (lastError) {
          setEmployeesError(`従業員データの取得に失敗しました: ${lastError.message}`);
          setActiveEmployees([]);
          return;
        }

        const normalized = (data ?? [])
          .map((row: Record<string, any>): EmployeeSummary | null => {
            const id = row.id ?? row.employee_id ?? '';
            const name = row.name ?? row.full_name ?? '';
            if (!id || !name) {
              return null;
            }

            const employmentType =
              row.employment_type ??
              row.employmentType ??
              row.department ??
              '未設定';
            const jobType =
              row.job_type ??
              row.jobType ??
              row.position ??
              '未設定';
            const statusValue =
              row.status === 'retired' ? 'retired' : 'active';

            const code =
              row.employee_code ??
              row.employeeCode ??
              row.employeeId ??
              row.employee_id ??
              null;

            return {
              id: String(id),
              name: String(name),
              employmentType: String(employmentType),
              jobType: String(jobType),
              employeeCode: code ? String(code) : undefined,
              status: statusValue
            };
          })
          .filter((item): item is EmployeeSummary => item !== null)
          .filter((item) => item.status === 'active');

        if (normalized.length === 0) {
          setEmployeesError('アクティブな従業員が登録されていません。');
          setActiveEmployees([]);
          return;
        }

        setActiveEmployees(normalized);
      } catch (err) {
        console.error('Failed to fetch employees', err);
        setEmployeesError('従業員データの取得処理で予期せぬエラーが発生しました。');
        setActiveEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchAttendanceRecords = async () => {
      setAttendanceLoading(true);
      setAttendanceError(null);

      try {
        if (!supabaseClient) {
          setAttendanceError('SupabaseのURLまたは匿名キーが設定されていません。`.env.local` を確認してください。');
          return;
        }

        const MAX_RETRIES = 5;
        let lastError: any = null;
        let data: any[] | null = null;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          const result = await supabaseClient
            .from('attendance_records')
            .select('*');

          if (!result.error) {
            data = result.data;
            lastError = null;
            break;
          }

          // Non-retryable errors
          if (result.error.code === '42P01' || result.error.code === '42501') {
            lastError = result.error;
            break;
          }

          lastError = result.error;
          console.warn(`[Retry ${attempt + 1}/${MAX_RETRIES}] attendance fetch failed:`, result.error.message);

          if (attempt < MAX_RETRIES - 1) {
            const delay = Math.min(2000 * Math.pow(2, attempt), 30000);
            await new Promise(r => setTimeout(r, delay));
          }
        }

        if (lastError) {
          if (lastError.code === '42P01') {
            setAttendanceError('attendance_records テーブルが存在しません。Supabase でテーブルを作成してください。');
          } else if (lastError.code === '42501') {
            setAttendanceError('勤怠データを取得する権限がありません。Supabase の RLS ポリシーを確認してください。');
          } else {
            setAttendanceError(`勤怠データの取得に失敗しました: ${lastError.message}`);
          }
          return;
        }

        const normalized: Record<string, AttendanceRecord> = {};

        (data ?? []).forEach((row: Record<string, any>) => {
          const normalizedRow = normalizeAttendanceRow(row as RemoteAttendanceRow);
          if (!normalizedRow) {
            return;
          }
          normalized[normalizedRow.key] = normalizedRow.record;
        });

        setAttendanceRecords(prev => ({
          ...prev,
          ...normalized
        }));
      } catch (err) {
        console.error('Failed to fetch attendance records', err);
        setAttendanceError('勤怠データの取得処理で予期せぬエラーが発生しました。');
      } finally {
        setAttendanceLoading(false);
      }
    };

    fetchAttendanceRecords();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ATTENDANCE_STORAGE_KEY, JSON.stringify(attendanceRecords));
    } catch (err) {
      console.warn('Failed to persist attendance cache', err);
    }
  }, [attendanceRecords]);

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatTimeHHMM = (date: Date): string => {
    return date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const formatWorkingTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}時間${mins}分`;
  };

  const calculateMinutesBetween = (startDate: Date, endDate: Date = new Date()): number => {
    const diffMs = endDate.getTime() - startDate.getTime();
    if (diffMs <= 0) {
      return 0;
    }
    return Math.floor(diffMs / 1000 / 60);
  };

  const broadcastAttendanceRecord = (
    record: AttendanceRecord,
    context: AttendanceBroadcastContext,
    fallbackId?: string
  ): void => {
    if (typeof window === 'undefined') return;
    const resolvedId = record.id ?? fallbackId ?? `${record.employeeCode}-${record.date}`;
    const payload: AttendanceBroadcastDetail = {
      record: { ...record, id: resolvedId },
      context,
      timestamp: new Date().toISOString()
    };
    window.dispatchEvent(new CustomEvent<AttendanceBroadcastDetail>(ATTENDANCE_EVENT_NAME, { detail: payload }));
  };

  // 勤務区分を自動判定（16時以降は夜勤）
  const determineShiftType = (clockInDate: Date): 'day' | 'night' => {
    const hour = clockInDate.getHours();
    const min = clockInDate.getMinutes();
    return (hour > 15 || (hour === 15 && min >= 30)) ? 'night' : 'day';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        employeeDropdownRef.current &&
        !employeeDropdownRef.current.contains(event.target as Node)
      ) {
        setEmployeeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const syncSelectedEmployeeAttendance = async () => {
      if (currentStep !== 'select' || !selectedEmployee || !supabaseClient) {
        return;
      }

      const matchedEmployee = activeEmployees.find(
        (emp) =>
          emp.id === selectedEmployee ||
          (emp.employeeCode && emp.employeeCode === selectedEmployee)
      );
      const employeeCodeForDb = matchedEmployee?.employeeCode ? String(matchedEmployee.employeeCode) : null;
      if (!matchedEmployee || !employeeCodeForDb) {
        return;
      }

      const lookupDates = buildRecentDateStrings(new Date(), RESUME_LOOKBACK_DAYS);
      if (lookupDates.length === 0) {
        return;
      }

      try {
        const { data, error } = await supabaseClient
          .from('attendance_records')
          .select('id, employee_id, employee_code, work_date, clock_in_at, clock_out_at, break_minutes, duty_type, status, notes, created_at, updated_at')
          .eq('employee_code', employeeCodeForDb)
          .in('work_date', lookupDates)
          .order('work_date', { ascending: false })
          .order('clock_in_at', { ascending: false });

        if (error) {
          throw error;
        }

        const replacements: Record<string, AttendanceRecord> = {};
        (data ?? []).forEach((row) => {
          const normalizedRow = normalizeAttendanceRow(row as RemoteAttendanceRow);
          if (!normalizedRow) {
            return;
          }
          replacements[normalizedRow.key] = normalizedRow.record;
        });

        setAttendanceRecords(prev =>
          replaceSelectedEmployeeAttendanceRecords(prev, matchedEmployee, lookupDates, replacements)
        );
      } catch (error) {
        console.error('Failed to sync selected employee attendance', error);
      }
    };

    syncSelectedEmployeeAttendance();
  }, [activeEmployees, currentStep, selectedEmployee]);

  const handleEmployeeCodeInputChange = (value: string): void => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
    setPendingPunchIn(null);
    setEmployeeCodeInput(digitsOnly);

    if (!digitsOnly) {
      setSelectedEmployee('');
      setEmployeeDropdownOpen(false);
      return;
    }

    const exactMatch = activeEmployees.find((emp) =>
      emp.employeeCode ? emp.employeeCode.toLowerCase() === digitsOnly.toLowerCase() : false
    );

    if (exactMatch) {
      const selectionValue = exactMatch.employeeCode ?? exactMatch.id;
      setSelectedEmployee(selectionValue);
      setEmployeeDropdownOpen(false);
    } else {
      setSelectedEmployee('');
      setEmployeeDropdownOpen(true);
    }
  };

  // 有給申請用の従業員コード入力ハンドラー
  const handleLeaveRequestEmployeeCodeChange = (value: string): void => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
    setLeaveRequestEmployeeCode(digitsOnly);

    if (!digitsOnly) {
      setLeaveRequestEmployee('');
      return;
    }

    const exactMatch = activeEmployees.find((emp) =>
      emp.employeeCode ? emp.employeeCode.toLowerCase() === digitsOnly.toLowerCase() : false
    );

    if (exactMatch) {
      const selectionValue = exactMatch.employeeCode ?? exactMatch.id;
      setLeaveRequestEmployee(selectionValue);
    } else {
      setLeaveRequestEmployee('');
    }
  };

  // 有給申請日数を計算
  const calculateLeaveDays = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  // 有給申請を送信
  const handleSubmitLeaveRequest = async (): Promise<void> => {
    if (!supabaseClient) {
      setLeaveRequestError('データベースに接続できません');
      return;
    }
    if (!leaveRequestEmployee) {
      setLeaveRequestError('従業員を選択してください');
      return;
    }
    if (!leaveStartDate || !leaveEndDate) {
      setLeaveRequestError('開始日と終了日を入力してください');
      return;
    }
    if (leaveStartDate > leaveEndDate) {
      setLeaveRequestError('開始日は終了日以前にしてください');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    if (leaveStartDate < today) {
      setLeaveRequestError('本日より前の日付は申請できません。過去の有給は管理者にご連絡ください。');
      return;
    }

    const matchedEmployee = activeEmployees.find(
      emp => emp.id === leaveRequestEmployee || emp.employeeCode === leaveRequestEmployee
    );

    if (!matchedEmployee?.employeeCode) {
      setLeaveRequestError('従業員コードが見つかりません');
      return;
    }

    setLeaveRequestLoading(true);
    setLeaveRequestError(null);

    try {
      const { error } = await supabaseClient
        .from('leave_requests')
        .insert({
          employee_code: matchedEmployee.employeeCode,
          start_date: leaveStartDate,
          end_date: leaveEndDate,
          status: 'pending'
        });

      if (error) throw error;

      setLeaveRequestSuccess(true);
      setTimeout(() => {
        setShowLeaveRequestModal(false);
        setLeaveRequestSuccess(false);
        setLeaveRequestEmployee('');
        setLeaveStartDate('');
        setLeaveEndDate('');
      }, 3000);
    } catch (err: any) {
      setLeaveRequestError(`申請に失敗しました: ${err?.message ?? String(err)}`);
    } finally {
      setLeaveRequestLoading(false);
    }
  };

  const handlePunchIn = async (): Promise<void> => {
    if (!selectedEmployee) {
      alert('従業員を選択してください');
      return;
    }

    if (punchActionInFlightRef.current) {
      return;
    }
    punchActionInFlightRef.current = true;
    setPunchSaving(true);

    try {

    // 既に出勤済みかチェック
    const lookbackDates = buildRecentDateStrings(new Date(), RESUME_LOOKBACK_DAYS);
    const today = lookbackDates[0] ?? getLocalDateString();
    const matchedEmployee = activeEmployees.find(
      (emp) =>
        emp.id === selectedEmployee ||
        (emp.employeeCode && emp.employeeCode === selectedEmployee)
    );
    if (!matchedEmployee) {
      alert('選択した従業員の情報が見つかりません。再度読み込みを行ってください。');
      return;
    }

    const employeeCodeForDb = matchedEmployee.employeeCode ? String(matchedEmployee.employeeCode) : null;

    if (!employeeCodeForDb) {
      setAttendanceError('従業員に社員コードが設定されていません。管理者に社員コードの登録を依頼してください。');
      return;
    }

    setAttendanceError(null);

    const employeeCodeForDisplay = employeeCodeForDb;
    const employeeName = matchedEmployee.name ?? employeeCodeForDisplay;
    const employeeInternalId = matchedEmployee.id ?? null;
    const recordPrimaryKey = employeeCodeForDisplay;
    const recordKeyCandidatesToday = buildRecordKeyCandidates(matchedEmployee, today);
    const existingRecordToday = findRecordByKeys(recordKeyCandidatesToday, attendanceRecords);
    const recordKeyToday = recordKeyCandidatesToday[0] ?? `${recordPrimaryKey}-${today}`;

    type RemoteAttendanceRow = {
      id: string;
      work_date: string | null;
      clock_in_at: string | null;
      clock_out_at: string | null;
      break_minutes: number | null;
      duty_type: string | null;
      status: string | null;
      notes: string | null;
      created_at: string | null;
      updated_at: string | null;
    };

    let remoteOpenRecord: RemoteAttendanceRow | null = null;
    let remoteRecordToday: RemoteAttendanceRow | null = null;

    if (supabaseClient) {
      try {
        const lookupDates = lookbackDates.length > 0 ? lookbackDates : [today];
        const { data, error } = await supabaseClient
          .from('attendance_records')
          .select('id, work_date, clock_in_at, clock_out_at, break_minutes, duty_type, status, notes, created_at, updated_at')
          .eq('employee_code', employeeCodeForDb)
          .in('work_date', lookupDates)
          .order('work_date', { ascending: false })
          .order('clock_in_at', { ascending: false });

        if (error) {
          throw error;
        }

        const rows = (data ?? []) as RemoteAttendanceRow[];
        remoteRecordToday = rows.find((row) => String(row.work_date ?? '') === today) ?? null;
        remoteOpenRecord = rows.find((row) => row.clock_in_at && !row.clock_out_at) ?? null;
      } catch (error: any) {
        console.error('Failed to fetch existing attendance before punch-in', error);
        setAttendanceError(`既存の勤怠データの確認に失敗しました: ${error?.message ?? String(error)}`);
        return;
      }
    }

    if (remoteOpenRecord && remoteOpenRecord.clock_in_at && !remoteOpenRecord.clock_out_at) {
      const restoredClockIn = new Date(remoteOpenRecord.clock_in_at);
      if (!Number.isNaN(restoredClockIn.getTime())) {
        const recordDate = remoteOpenRecord.work_date ? String(remoteOpenRecord.work_date) : today;
        const remoteClockInHHMM = formatTimeHHMM(restoredClockIn);

        // Skip stale remote records
        if (isStaleOpenRecord(recordDate, remoteClockInHHMM, new Date())) {
          console.warn('Supabaseの退勤漏れ記録をスキップして新規出勤します', {
            employee: selectedEmployee,
            staleRecordDate: recordDate,
            clockIn: remoteClockInHHMM
          });
        } else {
          const resumeShift = isShiftState(remoteOpenRecord.duty_type) ? remoteOpenRecord.duty_type : determineShiftType(restoredClockIn);
          const resumeBreakMinutes = Number(remoteOpenRecord.break_minutes ?? 0);
          const resumeRecord: AttendanceRecord = {
            id: String(remoteOpenRecord.id),
            employeeCode: employeeCodeForDisplay,
            employeeName,
            employeeInternalId,
            employee: matchedEmployee
              ? {
                employeeId: matchedEmployee.id ?? employeeCodeForDisplay,
                name: matchedEmployee.name
              }
              : null,
            date: recordDate,
            clockIn: remoteClockInHHMM,
            clockOut: null,
            breakMinutes: resumeBreakMinutes,
            totalMinutes: calculateMinutesBetween(restoredClockIn),
            shiftType: resumeShift,
            status: (remoteOpenRecord.status ?? 'working') as AttendanceStatus,
            notes: remoteOpenRecord.notes ?? null,
            createdAt: remoteOpenRecord.created_at ?? null,
            updatedAt: remoteOpenRecord.updated_at ?? null
          };
          const resumeKeyCandidates = buildRecordKeyCandidates(matchedEmployee, recordDate);
          const resumeKey = resumeKeyCandidates[0] ?? `${recordPrimaryKey}-${recordDate}`;
          setAttendanceRecords(prev => ({
            ...prev,
            [resumeKey]: resumeRecord
          }));
          setClockInTime(restoredClockIn);
          setWorkingMinutes(calculateMinutesBetween(restoredClockIn));
          setBreakMinutes(resumeBreakMinutes);
          setShiftType(resumeShift);
          setPunchTime(null);
          setPunchType('in');
          setPendingPunchIn(null);
          setCurrentStep('punched');
          broadcastAttendanceRecord(resumeRecord, 'resume', resumeKey);
          console.log('Supabaseの既存出勤記録を再開します', {
            employee: selectedEmployee,
            clockIn: resumeRecord.clockIn
          });
          return;
        }
      }
    }

    const hasCompletedAttendance = Boolean(remoteRecordToday?.clock_out_at);

    if (hasCompletedAttendance) {
      const confirmed =
        typeof window === 'undefined'
          ? true
          : window.confirm('本日は既に退勤済みです。再度出勤しますか？\n同じ日の再出勤は0からの勤務時間として記録されます。');
      if (!confirmed) {
        return;
      }
    }

    const reusablePendingPunchIn =
      pendingPunchIn?.employeeCode === employeeCodeForDb && pendingPunchIn.workDate === today
        ? pendingPunchIn
        : null;
    if (pendingPunchIn && !reusablePendingPunchIn) {
      setPendingPunchIn(null);
    }
    const now = reusablePendingPunchIn ? new Date(reusablePendingPunchIn.clockInAt) : new Date();
    if (Number.isNaN(now.getTime())) {
      setPendingPunchIn(null);
      setAttendanceError('保存待ちの出勤時刻を確認できませんでした。もう一度、出勤打刻を押してください。');
      return;
    }
    const detectedShiftType = reusablePendingPunchIn?.dutyType ?? determineShiftType(now);

    // DBへの保存と内容照合が終わるまでは、勤務中画面・ローカルキャッシュを更新しない。
    const existingRecordId = remoteRecordToday?.id
      ? String(remoteRecordToday.id)
      : existingRecordToday?.record?.id ?? null;
    const nextRecord: AttendanceRecord = {
      id: existingRecordId,
      employeeCode: employeeCodeForDisplay,
      employeeName,
      employeeInternalId,
      employee: matchedEmployee
        ? {
          employeeId: matchedEmployee.id ?? employeeCodeForDisplay,
          name: matchedEmployee.name
        }
        : null,
      date: today,
      clockIn: formatTimeHHMM(now),
      clockOut: null,
      breakMinutes: 0,
      totalMinutes: 0,
      shiftType: detectedShiftType,
      status: 'working',
      notes: existingRecordToday?.record?.notes ?? remoteRecordToday?.notes ?? null,
      createdAt: existingRecordToday?.record?.createdAt ?? remoteRecordToday?.created_at ?? new Date().toISOString(),
      updatedAt: existingRecordToday?.record?.updatedAt ?? remoteRecordToday?.updated_at ?? null
    };

    if (!supabaseClient) {
      setAttendanceError('データベースに接続できないため、出勤は保存されていません。通信状態を確認して再試行してください。');
      return;
    }

    try {
      setAttendanceError(null);
      const punchInInput: PendingPunchIn = reusablePendingPunchIn ?? {
        recordId: existingRecordId,
        employeeCode: employeeCodeForDb,
        workDate: today,
        clockInAt: now.toISOString(),
        clockOutAt: null,
        breakMinutes: 0,
        dutyType: detectedShiftType,
        status: 'working'
      };
      setPendingPunchIn(punchInInput);
      const persisted = await persistPunchIn(supabaseClient, punchInInput);

      const verifiedRecord: AttendanceRecord = {
        ...nextRecord,
        id: persisted.id,
        updatedAt: new Date().toISOString()
      };
      setWorkingMinutes(0);
      setPunchTime(now);
      setClockInTime(now);
      setShiftType(detectedShiftType);
      setPunchType('in');
      setAttendanceRecords(prev => ({
        ...prev,
        [recordKeyToday]: verifiedRecord
      }));
      broadcastAttendanceRecord(verifiedRecord, 'punch-in', recordKeyToday);
      setPendingPunchIn(null);
      setCurrentStep('punched');

      console.log('出勤打刻を保存・確認しました:', {
        employee: selectedEmployee,
        time: now,
        shiftType: detectedShiftType
      });
    } catch (err: any) {
      console.error('Failed to save punch-in record', err);
      setAttendanceError(`出勤の保存を確認できませんでした。勤務中画面には進んでいません。通信状態を確認して再試行してください。詳細: ${err?.message ?? String(err)}`);
    }
    } finally {
      punchActionInFlightRef.current = false;
      setPunchSaving(false);
    }
  };

  const handlePunchOut = (): void => {
    if (punchActionInFlightRef.current) {
      return;
    }
    setPendingPunchOut(null);
    setAttendanceError(null);
    setPunchType('out');
    if (workingMinutes >= 480) {
      setBreakMinutes(shiftType === 'night' ? 120 : 60);
    } else {
      setBreakMinutes(0);
    }
    setCurrentStep('break');
  };

  const confirmPunchOut = async (): Promise<void> => {
    if (punchActionInFlightRef.current) {
      return;
    }

    if (!clockInTime) {
      alert('出勤打刻が見つかりませんでした。最初からやり直してください。');
      resetToSelect();
      return;
    }

    punchActionInFlightRef.current = true;
    setPunchSaving(true);

    try {

    const now = pendingPunchOut ? new Date(pendingPunchOut.clockOutAt) : new Date();
    if (Number.isNaN(now.getTime())) {
      setPendingPunchOut(null);
      setAttendanceError('保存待ちの退勤時刻を確認できませんでした。もう一度、退勤を確定してください。');
      return;
    }

    // 夜勤対応: 出勤日を基準にDBを更新するため、出勤時刻から日付を取得
    const clockInDate = pendingPunchOut?.workDate ?? getDateStringFromDate(clockInTime);
    const finalShiftType = pendingPunchOut?.dutyType ?? resolveShiftTypeForPunchOut(clockInTime, now);
    const attemptedBreakMinutes = pendingPunchOut?.breakMinutes ?? breakMinutes;
    const actualWorkMinutes = Math.max(0, calculateMinutesBetween(clockInTime, now) - attemptedBreakMinutes);
    const matchedEmployee = activeEmployees.find(
      (emp) =>
        emp.id === selectedEmployee ||
        (emp.employeeCode && emp.employeeCode === selectedEmployee)
    );
    if (!matchedEmployee) {
      setAttendanceError('従業員の情報が見つかりません。従業員一覧を再読み込みしてください。');
      return;
    }

    let employeeCodeForDb = matchedEmployee.employeeCode ? String(matchedEmployee.employeeCode) : null;

    if (!employeeCodeForDb) {
      if (!supabaseClient) {
        setAttendanceError('従業員に社員コードが設定されていません。管理者に社員コードの登録を依頼してください。');
        return;
      }

      const generatedCode = generateEmployeeIdentifier();
      const { error: assignError } = await supabaseClient
        .from('employees')
        .update({ employeeId: generatedCode })
        .eq('id', matchedEmployee.id);

      if (assignError) {
        console.error('Failed to assign employee code automatically', assignError);
        setAttendanceError(`従業員コードの自動発番に失敗しました: ${assignError.message}`);
        return;
      }

      employeeCodeForDb = generatedCode;
      setActiveEmployees(prev =>
        prev.map(emp =>
          emp.id === matchedEmployee.id
            ? { ...emp, employeeCode: generatedCode }
            : emp
        )
      );
    }

    const employeeCodeForDisplay = employeeCodeForDb;
    if (pendingPunchOut && pendingPunchOut.employeeCode !== employeeCodeForDb) {
      setPendingPunchOut(null);
      setAttendanceError('保存待ちの退勤データと選択中の従業員が一致しません。勤務中画面に戻ってやり直してください。');
      return;
    }
    const employeeName = matchedEmployee.name ?? employeeCodeForDisplay;
    const employeeInternalId = matchedEmployee.id ?? null;
    const recordKeyCandidates = buildRecordKeyCandidates(matchedEmployee, clockInDate);
    const existingRecordForDate = findRecordByKeys(recordKeyCandidates, attendanceRecords);
    // 夜勤対応: ローカルキャッシュのキーも出勤日ベースにする
    const recordKey = existingRecordForDate?.key ?? recordKeyCandidates[0] ?? `${employeeCodeForDisplay}-${clockInDate}`;
    const formattedClockIn = formatTimeHHMM(clockInTime);
    const formattedClockOut = formatTimeHHMM(now);

    const prevRecord = existingRecordForDate?.record ?? attendanceRecords[recordKey];
    const punchOutInput: PendingPunchOut = pendingPunchOut ?? {
      recordId: prevRecord?.id ?? null,
      employeeCode: employeeCodeForDb,
      workDate: clockInDate,
      clockInAt: clockInTime.toISOString(),
      clockOutAt: now.toISOString(),
      breakMinutes: attemptedBreakMinutes,
      dutyType: finalShiftType,
      status: '確定'
    };
    setPendingPunchOut(punchOutInput);

    if (!supabaseClient) {
      setAttendanceError('データベースに接続できないため、退勤は保存されていません。通信状態を確認して再試行してください。');
      return;
    }

    try {
      setAttendanceError(null);
      const persisted = await persistPunchOut(supabaseClient, punchOutInput);

    const nextRecord: AttendanceRecord = {
      ...prevRecord,
      id: persisted.id,
      employeeCode: employeeCodeForDisplay,
      employeeName,
      employeeInternalId,
      employee: matchedEmployee
        ? {
          employeeId: matchedEmployee.id ?? employeeCodeForDisplay,
          name: matchedEmployee.name
        }
        : prevRecord?.employee ?? null,
      date: clockInDate,  // 夜勤対応: 出勤日を記録
      clockIn: formattedClockIn,
      clockOut: formattedClockOut,
      breakMinutes: attemptedBreakMinutes,
      totalMinutes: actualWorkMinutes,
      shiftType: finalShiftType,
      status: '確定',
      notes: prevRecord?.notes ?? null,
      createdAt: prevRecord?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setPunchTime(now);
    setShiftType(finalShiftType);
    setBreakMinutes(attemptedBreakMinutes);
    setAttendanceRecords(prev => ({
      ...prev,
      [recordKey]: nextRecord
    }));
    broadcastAttendanceRecord(nextRecord, 'punch-out', recordKey);
    setPendingPunchOut(null);
    setCompletedAttendanceId(persisted.id);
    setOvertimeEligible(null);
    setWorkingMinutes(calculateMinutesBetween(clockInTime, now));
    setCurrentStep('complete');

    console.log('退勤打刻を保存・確認しました:', {
      employee: selectedEmployee,
      time: now,
      breakMinutes: attemptedBreakMinutes,
      actualWorkMinutes,
      shiftType: finalShiftType,
      date: clockInDate
    });
    } catch (err: any) {
      console.error('Failed to save attendance record', err);
      if (err && typeof err === 'object' && err.code === 'conflict') {
        setAttendanceError(`別の退勤時刻が既に保存されています。既存データは上書きしていません。管理者に確認してください。詳細: ${err.message}`);
      } else {
        setAttendanceError(`退勤の保存を確認できませんでした。完了画面には進んでいません。通信状態を確認し、「同じ退勤時刻で再試行」を押してください。詳細: ${err?.message ?? String(err)}`);
      }
    }
    } finally {
      punchActionInFlightRef.current = false;
      setPunchSaving(false);
    }
  };

  const resetToSelect = ({ preserveSelection = false }: { preserveSelection?: boolean } = {}): void => {
    if (punchOutResetTimerRef.current) {
      clearTimeout(punchOutResetTimerRef.current);
      punchOutResetTimerRef.current = null;
    }
    punchActionInFlightRef.current = false;
    setPunchSaving(false);
    setPendingPunchIn(null);
    setPendingPunchOut(null);
    setCompletedAttendanceId(null);
    setOvertimeEligible(null);
    if (!preserveSelection) {
      setSelectedEmployee('');
      setEmployeeCodeInput('');
    }
    setAttendanceError(null);
    setEmployeeDropdownOpen(false);
    setCurrentStep('select');
    setPunchType('');
    setPunchTime(null);
    setBreakMinutes(0);
    setWorkingMinutes(0);
    setClockInTime(null);
    setShiftType('day');
    if (!preserveSelection && typeof window !== 'undefined') {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  };

  useEffect(() => {
    if (currentStep !== 'complete' || overtimeEligible !== false || !attendanceCalculation.config) return;
    punchOutResetTimerRef.current = setTimeout(() => resetToSelect(), 5000);
    return () => {
      if (punchOutResetTimerRef.current) {
        clearTimeout(punchOutResetTimerRef.current);
        punchOutResetTimerRef.current = null;
      }
    };
    // resetToSelect only resets local state; the timer restarts when the confirmed eligibility changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, overtimeEligible, attendanceCalculation.config]);

  // 従業員選択画面
  if (currentStep === 'select') {
    const selectedEmployeeSummary = resolveEmployee(selectedEmployee);
    const lookbackDates = buildRecentDateStrings(currentDateTime, RESUME_LOOKBACK_DAYS);
    const todayIso = lookbackDates[0] ?? getDateStringFromDate(currentDateTime);
    const yesterdayIso = lookbackDates[1] ?? getDateStringFromDate(new Date(currentDateTime.getTime() - 24 * 60 * 60 * 1000));
    const openRecordForSelection = selectedEmployeeSummary
      ? findOpenRecordByDates(selectedEmployeeSummary, lookbackDates, attendanceRecords)
      : null;
    const openRecord = openRecordForSelection?.record ?? null;
    const openRecordDate = openRecord?.date ?? null;
    const staleOpen = openRecord ? isStaleOpenRecord(openRecordDate, openRecord.clockIn, currentDateTime) : false;
    const openRecordDayLabel = openRecordDate
      ? openRecordDate === todayIso
        ? '本日'
        : openRecordDate === yesterdayIso
          ? '前日'
          : openRecordDate
      : null;
    const punchInLabel = (openRecord && !staleOpen) ? '退勤打刻へ' : '出勤打刻';

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className="text-center">
              <div className="text-5xl font-bold text-gray-900 font-mono mb-2">
                {formatTime(currentDateTime)}
              </div>
              <div className="text-lg text-gray-600">
                {formatDate(currentDateTime)}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Clock className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">勤怠管理システム</h1>
              <p className="text-gray-600">従業員を選択して打刻してください</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <div className="mb-10">
                <label className="block text-2xl font-medium text-gray-700 mb-4">
                  従業員コードを入力
                </label>
                <input
                  type="text"
                  value={employeeCodeInput}
                  onChange={(event) => handleEmployeeCodeInputChange(event.target.value)}
                  placeholder="例: 12345"
                  disabled={employeesLoading || activeEmployees.length === 0 || punchSaving || overtimeSaving}
                  maxLength={5}
                  inputMode="numeric"
                  pattern="\d*"
                  className={`w-full px-8 py-8 text-5xl border-2 rounded-xl transition-colors ${employeesLoading || activeEmployees.length === 0 || punchSaving || overtimeSaving
                    ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
                    : 'border-gray-300 focus:outline-none focus:border-blue-500 bg-white'
                    }`}
                />
                <p className="mt-3 text-lg text-gray-500">
                  従業員コードを入力すると自動的に従業員が選択されます。
                </p>
              </div>
              <label className="block text-2xl font-medium text-gray-700 mb-4">
                従業員名を選択
              </label>
              {employeesError && (
                <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg p-3">
                  {employeesError}
                </div>
              )}
              {attendanceError && (
                <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg p-3">
                  {attendanceError}
                </div>
              )}
              <div
                ref={employeeDropdownRef}
                className={`relative mb-10 ${employeesLoading || activeEmployees.length === 0 || punchSaving || overtimeSaving ? 'opacity-60 pointer-events-none' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => setEmployeeDropdownOpen((prev) => !prev)}
                  disabled={employeesLoading || activeEmployees.length === 0 || punchSaving || overtimeSaving}
                  className={`w-full px-8 py-8 text-4xl border-2 rounded-xl transition-colors flex justify-between items-center ${employeesLoading || activeEmployees.length === 0 || punchSaving || overtimeSaving
                    ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
                    : 'border-gray-300 focus:outline-none focus:border-blue-500 bg-white'
                    }`}
                >
                  <span>
                    {(() => {
                      if (employeesLoading) return '読み込み中...';
                      if (!selectedEmployee) return '選択してください';
                      const found = resolveEmployee(selectedEmployee);
                      return found?.name ?? '選択してください';
                    })()}
                  </span>
                  <svg
                    className={`w-10 h-10 ml-2 transition-transform ${employeeDropdownOpen ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {employeeDropdownOpen && !employeesLoading && !punchSaving && (
                  <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-96 overflow-y-auto">
                    <button
                      disabled={punchSaving || overtimeSaving}
                      onClick={() => {
                        setPendingPunchIn(null);
                        setSelectedEmployee('');
                        setEmployeeCodeInput('');
                        setEmployeeDropdownOpen(false);
                      }}
                      className="w-full text-left px-8 py-5 text-3xl hover:bg-gray-100 transition-colors"
                    >
                      選択してください
                    </button>
                    {(employeeCodeInput ? filteredEmployees : activeEmployees).map(emp => {
                      const value = emp.employeeCode ?? emp.id;
                      return (
                        <button
                          key={emp.id}
                          disabled={punchSaving || overtimeSaving}
                          onClick={() => {
                            setPendingPunchIn(null);
                            setSelectedEmployee(value);
                            setEmployeeCodeInput(emp.employeeCode ?? '');
                            setEmployeeDropdownOpen(false);
                          }}
                          className={`w-full text-left px-8 py-5 text-3xl transition-colors ${value === selectedEmployee ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100'
                            }`}
                        >
                          {emp.name}
                        </button>
                      );
                    })}
                    {employeeCodeInput && filteredEmployees.length === 0 && (
                      <div className="px-8 py-5 text-2xl text-gray-500">
                        該当する従業員が見つかりません。
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={handlePunchIn}
                disabled={!selectedEmployee || employeesLoading || attendanceLoading || punchSaving || overtimeSaving}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${selectedEmployee && !employeesLoading && !attendanceLoading && !punchSaving
                  ? (openRecord && !staleOpen)
                    ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:from-orange-600 hover:to-amber-700 shadow-lg hover:shadow-xl'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {(openRecord && !staleOpen) ? (
                  <LogOut className="w-6 h-6 inline mr-2" />
                ) : (
                  <LogIn className="w-6 h-6 inline mr-2" />
                )}
                {punchSaving
                  ? '保存内容を確認中...'
                  : pendingPunchIn
                    ? '同じ出勤時刻で再試行'
                    : punchInLabel}
              </button>
              {openRecord && !staleOpen && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  {openRecordDayLabel ? `${openRecordDayLabel}の出勤が継続中です。` : '出勤が継続中です。'}
                  {openRecord.clockIn ? ` 出勤時刻: ${openRecord.clockIn}` : ''}
                </div>
              )}
              {staleOpen && openRecord && (
                <div className="mt-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                  {openRecordDayLabel ?? openRecordDate}の退勤が未登録です。管理者にご連絡ください。
                </div>
              )}

              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowLeaveRequestModal(true)}
                  disabled={punchSaving || overtimeSaving}
                  className={`text-sm underline ${punchSaving
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-800'
                    }`}
                >
                  有給申請はこちら
                </button>
              </div>
              {selectedEmployeeSummary?.employeeCode && (
                <EmployeeOvertimeHistory key={selectedEmployeeSummary.id} supabaseClient={supabaseClient}
                  employeeCode={selectedEmployeeSummary.employeeCode} employeeName={selectedEmployeeSummary.name} disabled={punchSaving || overtimeSaving} onSavingChange={setOvertimeSaving} />
              )}
            </div>
          </div>
        </div>

        {/* 有給申請モーダル */}
        {showLeaveRequestModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl">
              <div className="px-8 py-5 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900">有給申請</h3>
                <button
                  onClick={() => {
                    setShowLeaveRequestModal(false);
                    setLeaveRequestError(null);
                    setLeaveRequestSuccess(false);
                    setLeaveRequestEmployee('');
                    setLeaveRequestEmployeeCode('');
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {leaveRequestSuccess ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">申請が完了しました</h4>
                  <p className="text-gray-600 text-sm">管理者の承認をお待ちください</p>
                </div>
              ) : (
                <div className="p-8 space-y-6">
                  {leaveRequestError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg p-3">
                      {leaveRequestError}
                    </div>
                  )}

                  <div>
                    <label className="block text-2xl font-medium text-gray-700 mb-3">
                      従業員コード <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={leaveRequestEmployeeCode}
                      onChange={(e) => handleLeaveRequestEmployeeCodeChange(e.target.value)}
                      placeholder="例: 1234"
                      disabled={leaveRequestLoading}
                      maxLength={5}
                      inputMode="numeric"
                      pattern="\d*"
                      className="w-full px-6 py-6 text-4xl border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500"
                    />
                    <p className="mt-2 text-base text-gray-500">
                      従業員コードを入力すると自動的に従業員が選択されます
                    </p>
                  </div>

                  <div>
                    <label className="block text-2xl font-medium text-gray-700 mb-3">
                      従業員名 <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={leaveRequestEmployee}
                      onChange={(e) => {
                        setLeaveRequestEmployee(e.target.value);
                        const emp = activeEmployees.find(emp => (emp.employeeCode ?? emp.id) === e.target.value);
                        setLeaveRequestEmployeeCode(emp?.employeeCode ?? '');
                      }}
                      disabled={leaveRequestLoading}
                      className="w-full px-6 py-6 text-3xl border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500"
                    >
                      <option value="">選択してください</option>
                      {activeEmployees.map(emp => (
                        <option key={emp.id} value={emp.employeeCode ?? emp.id}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xl font-medium text-gray-700 mb-3">
                        開始日 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={leaveStartDate}
                        onChange={(e) => setLeaveStartDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        disabled={leaveRequestLoading}
                        className="w-full px-4 py-4 text-2xl border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xl font-medium text-gray-700 mb-3">
                        終了日 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={leaveEndDate}
                        onChange={(e) => setLeaveEndDate(e.target.value)}
                        min={leaveStartDate || new Date().toISOString().split('T')[0]}
                        disabled={leaveRequestLoading}
                        className="w-full px-4 py-4 text-2xl border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {leaveStartDate && leaveEndDate && leaveStartDate <= leaveEndDate && (
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-xl text-blue-800">
                        申請日数: <span className="font-bold">{calculateLeaveDays(leaveStartDate, leaveEndDate)}日間</span>
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleSubmitLeaveRequest}
                    disabled={leaveRequestLoading || !leaveRequestEmployee || !leaveStartDate || !leaveEndDate}
                    className={`w-full py-5 rounded-xl font-bold text-2xl transition-all ${
                      !leaveRequestLoading && leaveRequestEmployee && leaveStartDate && leaveEndDate
                        ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {leaveRequestLoading ? '送信中...' : '申請する'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // 打刻後の画面（出勤中）
  if (currentStep === 'punched') {
    const employee = resolveEmployee(selectedEmployee);

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex flex-col">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className="text-center">
              <div className="text-5xl font-bold text-gray-900 font-mono mb-2">
                {formatTime(currentDateTime)}
              </div>
              <div className="text-lg text-gray-600">
                {formatDate(currentDateTime)}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-2xl">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{employee?.name}</h2>
                <div className="flex items-center justify-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    勤務中
                  </span>
                  <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${shiftType === 'night'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-blue-100 text-blue-800'
                    }`}>
                    {shiftType === 'night' ? '夜勤' : '日勤'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">出勤時刻</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {clockInTime ? formatTimeHHMM(clockInTime) : '--:--'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">勤務時間</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {displayWork ? formatWorkingTime(displayWork.minutes) : '集計確認中'}
                  </p>
                </div>
              </div>

              {attendanceCalculation.error && (
                <div role="alert" className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {attendanceCalculation.error}
                  <button type="button" onClick={attendanceCalculation.reload} className="ml-2 underline">集計設定を再確認</button>
                </div>
              )}
              {shiftType === 'night' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-6 text-sm text-purple-800">
                  <p>💡 16時以降の出勤のため、夜勤として記録されます</p>
                </div>
              )}

              <button
                onClick={handlePunchOut}
                className="w-full py-4 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-bold text-lg hover:from-red-600 hover:to-rose-700 transition-all shadow-lg hover:shadow-xl"
              >
                <LogOut className="w-6 h-6 inline mr-2" />
                退勤打刻
              </button>

              <button
                onClick={() => resetToSelect()}
                className="w-full mt-3 py-2 text-gray-600 hover:text-gray-900 text-sm transition-colors"
              >
                従業員選択に戻る
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 休憩時間入力画面
  if (currentStep === 'break') {
    const employee = resolveEmployee(selectedEmployee);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className="text-center">
              <div className="text-5xl font-bold text-gray-900 font-mono mb-2">
                {formatTime(currentDateTime)}
              </div>
              <div className="text-lg text-gray-600">
                {formatDate(currentDateTime)}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Coffee className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">休憩時間の入力</h2>
                <p className="text-gray-600">{employee?.name}</p>
              </div>

              {attendanceError && (
                <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg p-3">
                  {attendanceError}
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">出勤</p>
                    <p className="font-bold text-gray-900">
                      {clockInTime ? formatTimeHHMM(clockInTime) : '--:--'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">退勤</p>
                    <p className="font-bold text-gray-900">
                      {pendingPunchOut ? formatTimeHHMM(new Date(pendingPunchOut.clockOutAt)) : formatTimeHHMM(currentDateTime)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  休憩時間（分）
                </label>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[0, 30, 45, 60].map((minutes) => (
                    <button
                      key={minutes}
                      onClick={() => {
                        setPendingPunchOut(null);
                        setAttendanceError(null);
                        setBreakMinutes(minutes);
                      }}
                      disabled={punchSaving || overtimeSaving}
                      className={`py-3 rounded-lg text-sm font-medium transition-all ${breakMinutes === minutes
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        } ${punchSaving ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      {minutes === 0 ? 'なし' : `${minutes}分`}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={breakMinutes}
                    onChange={(e) => {
                      setPendingPunchOut(null);
                      setAttendanceError(null);
                      setBreakMinutes(Math.max(0, parseInt(e.target.value) || 0));
                    }}
                    disabled={punchSaving || overtimeSaving}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    min="0"
                    step="15"
                  />
                  <span className="text-gray-600 font-medium">分</span>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">総勤務時間</span>
                  <span className="font-bold text-gray-900">{displayWork ? formatWorkingTime(displayWork.minutes) : '集計確認中'}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">休憩時間</span>
                  <span className="font-bold text-gray-900">{breakMinutes}分</span>
                </div>
                <div className="border-t border-blue-200 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900">勤務時間（集計用）</span>
                    <span className="font-bold text-blue-600 text-lg">
                      {formatWorkingTime(Math.max(0, workingMinutes - breakMinutes))}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={confirmPunchOut}
                disabled={attendanceLoading || punchSaving || overtimeSaving}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${attendanceLoading || punchSaving || overtimeSaving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                  }`}
              >
                {punchSaving
                  ? '保存内容を確認中...'
                  : pendingPunchOut
                    ? '同じ退勤時刻で再試行'
                    : '退勤を確定'}
              </button>

              <button
                onClick={() => {
                  setPendingPunchOut(null);
                  setAttendanceError(null);
                  setCurrentStep('punched');
                }}
                disabled={punchSaving || overtimeSaving}
                className={`w-full mt-3 py-2 text-sm transition-colors ${punchSaving
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                戻る
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 完了画面
  if (currentStep === 'complete') {
    const employee = resolveEmployee(selectedEmployee);
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex flex-col">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className="text-center">
              <div className="text-5xl font-bold text-gray-900 font-mono mb-2">
                {formatTime(currentDateTime)}
              </div>
              <div className="text-lg text-gray-600">
                {formatDate(currentDateTime)}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                お疲れ様でした
              </h2>

              <p className="text-xl text-gray-700 mb-6">{employee?.name}</p>

              <div className="bg-gray-50 rounded-xl p-6 mb-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">勤務区分</span>
                  <span className={`font-bold ${shiftType === 'night' ? 'text-purple-600' : 'text-blue-600'
                    }`}>
                    {shiftType === 'night' ? '夜勤' : '日勤'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">出勤時刻</span>
                  <span className="font-bold text-gray-900">
                    {clockInTime ? formatTimeHHMM(clockInTime) : '--:--'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">退勤時刻</span>
                  <span className="font-bold text-gray-900">
                    {punchTime ? formatTimeHHMM(punchTime) : '--:--'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">休憩時間</span>
                  <span className="font-bold text-gray-900">{breakMinutes}分</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900">勤務時間（集計用）</span>
                    <span className="font-bold text-blue-600 text-xl">
                      {displayWork ? formatWorkingTime(displayWork.minutes) : '集計確認中'}
                    </span>
                  </div>
                </div>
              </div>

              {displayWork && displayWork.breakMinutes !== breakMinutes && (
                <p className="mb-3 text-sm text-gray-600">集計上の休憩時間: {displayWork.breakMinutes}分（勤務時間に応じた設定を適用）</p>
              )}
              {attendanceCalculation.error && (
                <div role="alert" className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {attendanceCalculation.error}
                  <button type="button" onClick={attendanceCalculation.reload} className="ml-2 underline">集計設定を再確認</button>
                </div>
              )}
              {employee?.employeeCode && completedAttendanceId && (
                <EmployeeOvertimePanel supabaseClient={supabaseClient} employeeCode={employee.employeeCode}
                  employeeName={employee.name} attendanceId={completedAttendanceId} onEligibilityResolved={setOvertimeEligible} onSavingChange={setOvertimeSaving} />
              )}
              {overtimeEligible === false && attendanceCalculation.config && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800"><Clock className="w-4 h-4 inline mr-1" />5秒後に自動的に戻ります</p>
                </div>
              )}

              <button
                onClick={() => resetToSelect()}
                disabled={overtimeSaving}
                className="disabled:opacity-50 px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                すぐに次の従業員で打刻
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default EmployeeAttendanceTool;
